import React, { useEffect, useState } from 'react';
import Pagination from "./ui/Pagination";
import { useSelector } from "react-redux";
import { selectCustomPagination } from "../features/paginationStore";
import { useQueryParams } from "../hooks/useQueryParams";
import CustomTable from "./ui/DataTable";
import useFetch from "../hooks/useFetch";
import { TailSpin } from "react-loader-spinner";
import { selectCid } from '../features/companyid';
import { formatDateToShortMonth } from '../utils/dateUtils';
import AssignCandidateModal from './modal/AssignCandidateModal';
import { toast } from 'react-toastify';

const NotMatchedTable = () => {
    const pageKey = "not_matched_candidates";
    const { page, page_size } = useSelector((state) => selectCustomPagination(state, pageKey));
    const { query, setQueryParams } = useQueryParams();
    const cid = useSelector(selectCid);

    const { data: allJobsData } = useFetch(
        `/api/v1/internal-sourcing/jobs` +
        `?company_id=${cid}` +
        `&page=-1` +
        `&page_size=-1`
    );


    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [deletingUuid, setDeletingUuid] = useState(null);

    const { send: bulkDeleteCandidates } = useFetch(
        '/api/v1/internal-sourcing/unmatched-candidates',
        { method: 'DELETE', headers: { 'Content-Type': 'application/json' } },
        false
    );

    const handleBulkDelete = async () => {
        if (selectedRows.length === 0) return;
        setIsBulkDeleting(true);
        try {
            const res = await bulkDeleteCandidates({
                company_id: cid,
                candidate_uuids: selectedRows,
            });
            if (res?.status === 'removed') {
                toast.success(`${res.candidates_removed} candidate(s) removed`);
                setSelectedRows([]);
                refetch();
            } else if (res?.error) {
                toast.error(res.error);
            }
        } catch {
            toast.error('Failed to delete candidates');
        } finally {
            setIsBulkDeleting(false);
        }
    };

    const handleSingleDelete = async (candidateUuid) => {
        setDeletingUuid(candidateUuid);
        try {
            const res = await bulkDeleteCandidates({
                company_id: cid,
                candidate_uuids: [candidateUuid],
            });
            if (res?.status === 'removed') {
                toast.success('Candidate removed');
                setSelectedRows((prev) => prev.filter((id) => id !== candidateUuid));
                refetch();
            } else if (res?.error) {
                toast.error(res.error);
            }
        } catch {
            toast.error('Failed to delete candidate');
        } finally {
            setDeletingUuid(null);
        }
    };

    useEffect(() => {
        if (query.page !== String(page) || query.page_size !== String(page_size)) {
            setQueryParams({ page: String(page), page_size: String(page_size) });
        }
    }, [page, page_size, query.page, query.page_size, setQueryParams]);

    const { send: refetchManualAssign } = useFetch(`/api/v1/internal-sourcing/manual-assign`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
    }, false)

    const { data, isLoading, refetch } = useFetch(
        `/api/v1/internal-sourcing/unmatched-candidates` +
        `?company_id=${cid}` +
        `&page=${page}` +
        `&page_size=${page_size}`,
        {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    const candidates = data?.candidates || [];
    const totalPages = data?.pagination?.total_pages || 1;

    const columns = [
        {
            key: "applicant",
            header: (
                <div className="flex items-center gap-4 text-left">
                    <span>Applicant</span>
                </div>
            ),
            notCentered: true,
            width: "25%",
            body: (row) => (
                <div className="flex items-center gap-4 text-left ml-2">
                    <div className="flex flex-col">
                        <span
                            className="font-medium text-web-blue cursor-pointer hover:underline"
                            onClick={() => row.resume_url && window.open(row.resume_url, '_blank', 'noopener,noreferrer')}
                        >{row.name || "Anonymous"}</span>
                    </div>
                </div>
            )
        },
        {
            key: "experience",
            header: "Experience",
            width: "15%",
            body: (row) => <span className="text-gray-600">{row.total_experience ? `${row.total_experience}` : '-'}</span>
        },
        {
            key: "highest_education",
            header: "Highest Education",
            width: "15%",
            body: (row) => <span className="text-gray-600">{row.highest_education || '-'}</span>
        },
        {
            key: "uploadDate",
            header: "Upload date",
            width: "15%",
            body: (row) => <span className="text-gray-600">{row.upload_date ? formatDateToShortMonth(row.upload_date) : '-'}</span>
        },
        {
            key: "actions",
            header: "",
            width: "15%",
            body: (row) => (
                <div className="flex justify-end pr-4 gap-2">
                    <button
                        onClick={() => {
                            setSelectedCandidate(row);
                            setIsAssignModalOpen(true);
                        }}
                        className="bg-[#1C2434] text-white px-6 py-2 rounded-md text-sm font-semibold hover:bg-[#2C3852] transition-colors shadow-sm"
                    >
                        Assign
                    </button>
                    <button
                        onClick={() => handleSingleDelete(row.candidate_uuid)}
                        disabled={deletingUuid === row.candidate_uuid || isBulkDeleting}
                        className="bg-red-50 text-red-500 px-6 py-2 rounded-md text-sm font-semibold hover:bg-red-100 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                        {deletingUuid === row.candidate_uuid ? (
                            <TailSpin color="#ef4444" height={16} width={16} />
                        ) : 'Delete'}
                    </button>
                </div>
            )
        }
    ];

    if (isLoading) {
        return (
            <div className="w-full flex justify-center py-20 mt-6 bg-white border border-gray-200 rounded-xl shadow-sm">
                <TailSpin color="blue" height={60} width={60} />
            </div>
        );
    }
    return (
        <div className="w-full flex flex-col mt-6 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            {selectedRows.length > 0 && (
                <div className="flex items-center justify-between px-4 py-3 bg-blue-50 border-b border-blue-200">
                    <span className="text-sm text-primaryBlack font-medium">
                        {selectedRows.length} candidate{selectedRows.length > 1 ? 's' : ''} selected
                    </span>
                    <button
                        onClick={handleBulkDelete}
                        disabled={isBulkDeleting}
                        className="bg-red-500 text-white px-5 py-2 rounded-md text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isBulkDeleting && <TailSpin color="#ffffff" height={16} width={16} />}
                        {isBulkDeleting ? 'Deleting...' : 'Delete Selected'}
                    </button>
                </div>
            )}
            <CustomTable
                columns={columns}
                tableData={candidates}
                noBorder={true}
                className="!px-0"
                tableClass="!my-0 !rounded-none"
                hasCheckboxes={true}
                selectedRows={selectedRows}
                onSelectedRowsChange={setSelectedRows}
                rowKey="candidate_uuid"
            />
            <div className="bg-white px-2 pt-4 pb-4 border-t border-gray-200">
                <Pagination pageKey={pageKey} totalPages={totalPages} className="!bg-transparent" />
            </div>

            {isAssignModalOpen && selectedCandidate && (
                <AssignCandidateModal
                    candidate={selectedCandidate}
                    onClose={() => {
                        setIsAssignModalOpen(false);
                        setSelectedCandidate(null);
                    }}
                    onConfirm={refetchManualAssign}
                    allJobs={allJobsData}
                />
            )}
        </div>
    );
};

export default NotMatchedTable;
